import { authFetch, BASE_BACKEND_URL } from '../../../auth'
import { useState, useEffect, useRef } from 'react'
import LoadingSpinner from '../../../components/LoadingSpinner'
import usePagination from '../../../layout/usePagination'
import { DEFAULT_PER_PAGE } from '../../../constants/constant'

import ClientDashboard from '../components/ClientDashboard'
import ClientHeader from '../components/header/ClientHeader'

const Client360Tab = ({
  clientData,
  isClientFetching,
  downloadData,
  setDownloadData,
  isDownloadDataFetching,
  setIsDownloadDataFetching,
  startDate,
  endDate,
  selectedDates,
  DefaultDates,
  handleDateFilter,
  resetDates
}) => {
  const [isTableDataFetching, setIsTableDataFetching] = useState(true)
  const [isCardDataFetching, setIsCardDataFetching] = useState(true)
  const [isFilterDataFetching, setIsFilterDataFetching] = useState(true)
  const [hasError, setHasError] = useState(false)

  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: DEFAULT_PER_PAGE
  })
  const [totalPages, setTotalPages] = useState(0)

  const [filterQuery, setFilterQuery] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const [cardData, setCardData] = useState(null)
  const [tableData, setTableData] = useState(null)
  const [usersFilterData, setUsersFilterData] = useState(null)

  const isFetching = isClientFetching || cardData === null

  const client_uid = clientData?.db_ref

  // AbortControllers separados
  const abortTableRef = useRef(null)
  const abortCardRef = useRef(null)
  const abortFilterRef = useRef(null)
  const abortDownloadRef = useRef(null)

  useEffect(() => {
    const handleBeforeUnload = () => {
      abortTableRef.current?.abort('Page unload')
      abortCardRef.current?.abort('Page unload')
      abortFilterRef.current?.abort('Page unload')
      abortDownloadRef.current?.abort('Page unload')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      abortTableRef.current?.abort()
      abortCardRef.current?.abort()
      abortFilterRef.current?.abort()
      abortDownloadRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    fetchUsersFilterDataFromServer()
  }, [])

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current_page: 1 }))
    fetchTableDataFromServer()
    fetchCardDataFromServer()
    fetchDownloadDataFromServer()
  }, [startDate, endDate])

  useEffect(() => {
    fetchTableDataFromServer()
  }, [searchQuery, filterQuery])

  useEffect(() => {
    fetchTableDataFromServer()
  }, [pagination.current_page])

  const fetchTableDataFromServer = async (overrideStart, overrideEnd) => {
    if (abortTableRef.current) {
      abortTableRef.current.abort()
    }
    const controller = new AbortController()
    abortTableRef.current = controller
    setIsTableDataFetching(true)
    setHasError(false)
    const start = overrideStart ?? startDate
    const end = overrideEnd ?? endDate

    await authFetch(
      `${BASE_BACKEND_URL}/reports/client_detailed_report?client_uid=${client_uid}&start_date=${start}&end_date=${end}&page=${pagination.current_page}&per_page=${DEFAULT_PER_PAGE}
        ${filterQuery || searchQuery ? '&filter_search=true' : ''}${filterQuery ? filterQuery : ''}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`,
      {
        method: 'GET',
        signal: controller.signal
      }
    )
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        if (!data) return
        setTableData(data.table)
        const pages = Math.ceil((data.count || 0) / DEFAULT_PER_PAGE)
        setTotalPages(pages)
        setPagination((prev) => ({ ...prev, total_pages: pages }))
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Error fetchTableDataFromServer:', error)
          setHasError(true)
          setTableData([])
          setTotalPages(0)
          setPagination((prev) => ({ ...prev, total_pages: 0 }))
        }
      })
    setIsTableDataFetching(false)
  }

  const fetchCardDataFromServer = async (overrideStart, overrideEnd) => {
    if (abortCardRef.current) {
      abortCardRef.current.abort()
    }
    const controller = new AbortController()
    abortCardRef.current = controller
    setIsCardDataFetching(true)
    setHasError(false)
    const start = overrideStart ?? startDate
    const end = overrideEnd ?? endDate

    await authFetch(
      `${BASE_BACKEND_URL}/reports/client_detailed_report?client_uid=${client_uid}&card_info_only=true&start_date=${start}&end_date=${end}`,
      {
        method: 'GET',
        signal: controller.signal
      }
    )
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        if (!data) return
        setCardData(data)
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Error fetchCardDataFromServer:', error)
          setHasError(true)
          setCardData(null)
        }
      })
    setIsCardDataFetching(false)
  }

  const fetchUsersFilterDataFromServer = async () => {
    if (abortFilterRef.current) {
      abortFilterRef.current.abort()
    }
    const controller = new AbortController()
    abortFilterRef.current = controller
    setIsFilterDataFetching(true)
    setHasError(false)

    await authFetch(`${BASE_BACKEND_URL}/ubiqua_app_users?non_paginated=true`, {
      method: 'GET',
      signal: controller.signal
    })
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        if (!data) return
        setUsersFilterData(
          data.table.map((u) => ({
            value: u.uid,
            label: `${u.uid} - ${u.full_name}`
          }))
        )
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Error fetchUsersFilterDataFromServer:', error)
          setHasError(true)
          setUsersFilterData([])
        }
      })

    setIsFilterDataFetching(false)
  }

  const fetchDownloadDataFromServer = async () => {
    if (abortDownloadRef.current) {
      abortDownloadRef.current.abort()
    }
    const controller = new AbortController()
    abortDownloadRef.current = controller
    setIsDownloadDataFetching(true)

    await authFetch(
      `${BASE_BACKEND_URL}/reports/client_detailed_report?client_uid=${client_uid}&download_info_only=true&start_date=${startDate}&end_date=${endDate}`,
      {
        method: 'GET',
        signal: controller.signal
      }
    )
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        if (!data) return
        setDownloadData(data.table)
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Error fetchDownloadDataFromServer:', error)
          setDownloadData(null)
        }
      })
    setIsDownloadDataFetching(false)
  }

  return (
    <>
      {hasError && !isFetching && (
        <div className="alert alert-message alert-danger" role="alert">
          <strong>Hubo un error al traer los datos. Intenta nuevamente.</strong>
        </div>
      )}
      {isFetching ? (
        <div className="loading-fullscreen">
          <div className="loading-card">
            <LoadingSpinner text="Cargando..." />
          </div>
        </div>
      ) : (
        <>
          <div className="container-fluid mt-3">
            <ClientHeader
              clientData={cardData.client_info}
              isCardDataFetching={isCardDataFetching}
            />

            <ClientDashboard
              clientId={clientData?.id}
              tableData={tableData}
              cardData={cardData}
              usersFilterData={usersFilterData}
              currentPage={pagination.current_page}
              setPagination={setPagination}
              totalPages={totalPages}
              handlePaginationClick={handlePaginationClick}
              searchQuery={searchQuery}
              onSearchQuery={(query) => {
                setSearchQuery(query)
                setPagination((prev) => ({ ...prev, current_page: 1 }))
              }}
              filterQuery={filterQuery}
              setFilterQuery={setFilterQuery}
              isTableDataFetching={isTableDataFetching}
              isCardDataFetching={isCardDataFetching}
              isFilterDataFetching={isFilterDataFetching}
              startDate={startDate}
              endDate={endDate}
              DefaultDates={DefaultDates}
              handleDateFilter={handleDateFilter}
              selectedDates={selectedDates}
              resetDates={resetDates}
            />
          </div>
        </>
      )}
    </>
  )
}

export default Client360Tab
